import { db, auth } from '../firebaseConfig.js';
import { collection, query, where, getDocs, addDoc, serverTimestamp, orderBy, limit, startAfter, getDocsFromCache } from 'firebase/firestore';
import { ensureFreshToken } from './authUtils.js';

// Get all reasons for a solution (including counter-reasons)
export async function getSolutionReasons(solutionId, reasonType = 'all', opts = {}) {
  const { pageSize = 50, cursor = null, fetchPolicy = 'cache-first' } = opts;
  const reasonsRef = collection(db, 'solution_reasons');
  
  try {
    // For counter-reasons system, we fetch all reasons for the solution
    const q = cursor
      ? query(
          reasonsRef,
          where('solutionId', '==', solutionId),
          orderBy('timestamp', 'desc'),
          startAfter(cursor),
          limit(pageSize)
        )
      : query(
          reasonsRef,
          where('solutionId', '==', solutionId),
          orderBy('timestamp', 'desc'),
          limit(pageSize)
        );
    // Cache-first policy
    if (fetchPolicy === 'cache-only' || fetchPolicy === 'cache-first') {
      try {
        const cachedSnap = await getDocsFromCache(q);
        if (!cachedSnap.empty || fetchPolicy === 'cache-only') {
          const reasons = cachedSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          const nextCursor = cachedSnap.docs.length === pageSize ? cachedSnap.docs[cachedSnap.docs.length - 1] : null;
          return { solutionId, reasonType, reasons, nextCursor, source: 'cache' };
        }
      } catch (_) {}
    }
    const snapshot = await getDocs(q);
    const reasons = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    const nextCursor = snapshot.docs.length === pageSize ? snapshot.docs[snapshot.docs.length - 1] : null;
    return { solutionId, reasonType, reasons, nextCursor, source: 'server' };
  } catch (err) {
    // Fallback without orderBy when composite index is missing
    const q = cursor
      ? query(reasonsRef, where('solutionId', '==', solutionId), startAfter(cursor), limit(pageSize))
      : query(reasonsRef, where('solutionId', '==', solutionId), limit(pageSize));
    if (fetchPolicy === 'cache-only' || fetchPolicy === 'cache-first') {
      try {
        const cachedSnap = await getDocsFromCache(q);
        if (!cachedSnap.empty || fetchPolicy === 'cache-only') {
          const reasons = cachedSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          const nextCursor = cachedSnap.docs.length === pageSize ? cachedSnap.docs[cachedSnap.docs.length - 1] : null;
          return { solutionId, reasonType, reasons, nextCursor, source: 'cache' };
        }
      } catch (_) {}
    }
    const snapshot = await getDocs(q);
    const reasons = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    const nextCursor = snapshot.docs.length === pageSize ? snapshot.docs[snapshot.docs.length - 1] : null;
    return { solutionId, reasonType, reasons, nextCursor, source: 'server' };
  }
}

// Submit a new reason or counter-reason
export async function submitSolutionReason({ solutionId, userId, userName, userProfession, reasonType, text, title = '', parentReasonId = null }) {
  // Validate input
  const allowedTypes = ['general', 'canWork', 'cantWork', 'wontWork']; // Keep legacy support
  const trimmedText = (text || '').toString().trim();
  const trimmedTitle = (title || '').toString().trim();
  if (!solutionId || !trimmedText || trimmedText.length > 2000 || !allowedTypes.includes(reasonType)) {
    const err = new Error('Invalid reason payload');
    console.error('[submitSolutionReason] Invalid payload', { solutionId, hasText: !!trimmedText, len: trimmedText.length, reasonType });
    throw err;
  }

  // Enforce auth and override userId with authenticated UID
  const authUid = auth?.currentUser?.uid;
  if (!authUid) {
    const err = new Error('Not authenticated');
    console.error('[submitSolutionReason] No authenticated Firebase user present');
    throw err;
  }
  const uidToUse = authUid;
  if (userId && userId !== authUid) {
    console.warn('[submitSolutionReason] userId mismatch (overriding with auth.uid)', { providedUserId: userId, authUid });
  }

  const reasonsRef = collection(db, 'solution_reasons');
  const payload = {
    solutionId,
    userId: uidToUse,
    userName: userName ?? 'User',
    userProfession: userProfession ?? '',
    reasonType,
    text: trimmedText,
    timestamp: serverTimestamp(),
    likesCount: 0,
    commentsCount: 0,
    ...(trimmedTitle && { title: trimmedTitle }),
    ...(parentReasonId && { parentReasonId }) // Add parentReasonId for counter-reasons
  };

  try {
    await ensureFreshToken();
    const docRef = await addDoc(reasonsRef, payload);
    return {
      solutionId,
      reason: {
        id: docRef.id,
        solutionId,
        userId: uidToUse,
        userName: payload.userName,
        userProfession: payload.userProfession,
        reasonType,
        text: trimmedText,
        ...(trimmedTitle && { title: trimmedTitle }),
        timestamp: new Date(),
        likesCount: 0,
        commentsCount: 0,
        ...(parentReasonId && { parentReasonId })
      }
    };
  } catch (error) {
    console.error('[submitSolutionReason] Firestore write failed', { code: error?.code, message: error?.message });
    throw error;
  }
}
